import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="appointments"
// Handles availability loading and direct appointment booking (Care Nav flow).
export default class extends Controller {
  static targets = ["visitReason", "location", "availabilityContainer", "bookingForm", "startTimeField"]
  static values = {
    npi: String,
    chatId: String,
    messageId: String,
    memberId: String
  }

  connect() {
    // Load initial availability on connect
    this.refreshAvailability()
  }

  refreshAvailability() {
    const visitReasonId = this.hasVisitReasonTarget ? this.visitReasonTarget.value : null
    const providerLocationId = this.hasLocationTarget ? this.locationTarget.value : null
    const npi = this.npiValue
    const chatId = this.chatIdValue
    const messageId = this.messageIdValue

    if (!visitReasonId || !providerLocationId || !npi) {
      console.error("Missing required parameters for availability check")
      return
    }

    // Show loading spinner
    this.availabilityContainerTarget.innerHTML = `
      <div class="flex justify-center py-8">
        <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    `

    // Build URL with all parameters including chat context
    const params = new URLSearchParams({
      npi: npi,
      provider_location_id: providerLocationId,
      visit_reason_id: visitReasonId
    })

    // Add optional chat context if available
    if (chatId) params.append('chat_id', chatId)
    if (messageId) params.append('message_id', messageId)
    if (this.memberIdValue) params.append('member_id', this.memberIdValue)

    const url = `/search/availability?${params.toString()}`

    fetch(url)
      .then(response => response.text())
      .then(html => {
        this.availabilityContainerTarget.innerHTML = html
      })
      .catch(error => {
        console.error("Error fetching availability:", error)
        this.availabilityContainerTarget.innerHTML = `
          <div class="bg-alert-red-200 rounded-lg p-4 text-center">
            <p class="text-[14px] text-alert-red-700">
              Failed to load availability. Please try again.
            </p>
          </div>
        `
      })
  }

  // Called when the user changes the "For" member select.
  // Stores the selected member ID and re-fetches availability with that member's info.
  selectMember(event) {
    this.memberIdValue = event.currentTarget.value
    this.refreshAvailability()
  }

  // Called after the patient info form's Turbo submission completes.
  // If the save succeeded (2xx), re-fetch availability so the form disappears
  // and the time-slot buttons become enabled.
  onPatientInfoSaved(event) {
    const fetchResponse = event.detail?.fetchResponse
    if (fetchResponse && fetchResponse.succeeded) {
      this.refreshAvailability()
    }
  }

  // Book a time slot directly (Care Nav flow - no interstitial countdown drawer).
  // Called when the user clicks a time slot button. The button carries the start_time
  // as a data attribute; all other booking params are in the hidden form rendered by
  // _availability_slots.html.erb.
  //
  // This method only handles visual feedback and form submission. The form has
  // data-turbo-stream="true", so Turbo handles the POST. The server responds with
  // Turbo Streams that append the card to the chat and close all drawers.
  bookSlot(event) {
    const button = event.currentTarget
    const startTime = button.dataset.startTime

    if (!startTime) {
      console.error("Missing start_time on time slot button")
      return
    }

    // Prevent double-clicks
    if (this._booking) return
    this._booking = true

    // Visual feedback: disable all slot buttons
    this.element.querySelectorAll('[data-action*="bookSlot"]').forEach(btn => {
      btn.disabled = true
      btn.classList.add('opacity-50')
    })
    button.textContent = 'Booking...'

    // Fill in the start_time and submit -- Turbo handles the rest
    this.startTimeFieldTarget.value = startTime
    this.bookingFormTarget.requestSubmit()
  }
}
